import React, { useCallback, useEffect, useMemo, useState } from "react";
import { map, take, times } from "lodash";
import { Spring } from "react-spring/renderprops";
import { List } from "react-virtualized";
import styled, { css, keyframes } from "styled-components";
import { getScreenSize } from "@charty/hooks";
import Pagination from "@charty/pagination";
import { FontWeight, border, content } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { usePages, useSize } from "@lkf/hooks";
import Icon from "@ui-kit/icon";
import Menu from "@ui-kit/popover/menu";
import { tr } from "@radarly/i18n";
import { SearchableInfluencer } from "@radarly/models/influencer";
import { getMetricFromSort } from "@radarly/models/influencer/generic";
import { SortInfluencer } from "@radarly/models/search";
import Infoline from "./infoline";

export interface IProps {
  locale?: Locale;
  influencers: SearchableInfluencer[];
  limit?: number;
  sort?: SortInfluencer;
  onSort: (sort: SortInfluencer) => void;
  onClickInfluencer?: (influencer: SearchableInfluencer) => void;
  className?: string;
  isFullscreen?: boolean;
  tooltip: (influencer: SearchableInfluencer) => React.ReactNode;
}

const Header = styled.div`
  font-weight: ${FontWeight.Normal};
  border-bottom: 1px solid ${border};
  display: flex;
  align-items: center;
  padding: 0 12px;
`;

const Accounts = styled.div<{ largeScreen: boolean }>`
  margin-left: -12px;
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: ${({ largeScreen }) =>
    largeScreen ? "calc(50% + 12px)" : "calc(100% + 12px)"};
  display: flex;
  align-items: center;
  padding-bottom: 4px;
`;

const Labels = styled.div`
  width: 100%;
  display: flex;
  padding-bottom: 4px;
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  top: 0;
  left: -20px;
`;

const HeaderText = styled.div<{ clickable: boolean }>`
  color: ${(props) => content(props, "secondary")};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  display: flex;
  align-items: center;
`;

const Label = styled(HeaderText)<{ selected: boolean }>`
  width: 33%;
  color: ${(props) => props.selected && content(props, "primary")};
  position: relative;
`;

const InfluencersContainer = styled.div<{ height: string }>`
  max-height: ${(props) => props.height};
  overflow: hidden !important;
`;

const StyledPagination = styled(Pagination)`
  margin-top: 16px;
`;

const appear = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const StyledInfoline = styled(Infoline)<{
  index: number;
  delay: number;
  clickable: boolean;
  animate?: boolean;
}>`
  border-bottom: ${(props) => `1px solid ${content(props, "tertiary")}`};
  margin-top: -1px;
  animation: ${(props) =>
    props.animate &&
    css`
      ${appear} ${props.delay}ms ease
    `};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
`;

const getUpperMultiple = (length: number, limit: number) =>
  limit * Math.ceil(length / limit);

const RadarlyWidgetTopAuthors = ({
  locale = "en_GB",
  influencers,
  limit = 3,
  onSort,
  sort,
  onClickInfluencer,
  className,
  isFullscreen,
  tooltip,
}: IProps) => {
  const [useDynamicList, setUseDynamicList] = useState(false);
  const ref = React.useRef();
  const { width } = useSize(ref);
  const lineHeight = 83;
  const animationDelay = 800;
  const screenSize = getScreenSize(width);
  const postsLabel = tr(locale, "INFLUENCERS::TABLE::COLUMN::POSTS");
  const estimatedReachLabel = tr(locale, "INFLUENCERS::TABLE::COLUMN::REACH");
  const engagementLabel = tr(
    locale,
    "INFLUENCERS::TABLE::COLUMN::ENGAGEMENTSHORT"
  );
  const length = influencers.length;
  const showPagination = !isFullscreen && length > limit;
  const { page, onPageReset, onPageUp, onPageDown } = usePages(length, limit);
  const mainUnit = getMetricFromSort(sort);
  const formattedInfluencers = useMemo(() => {
    const upperMultiple = getUpperMultiple(influencers.length, limit);
    const missingRows = upperMultiple - influencers.length;
    if (missingRows === 0 || isFullscreen) {
      return influencers;
    } else {
      return [
        ...influencers,
        ...times(missingRows, () => {
          return { ...influencers[0], isPhantom: true };
        }),
      ];
    }
  }, [influencers, limit, isFullscreen]);

  function rowRenderer({ key, index, style, isScrolling }) {
    return (
      <div
        key={key}
        style={{ ...style, cursor: !onClickInfluencer && "default" }}
        onClick={
          onClickInfluencer
            ? () => onClickInfluencer(formattedInfluencers[index])
            : null
        }
      >
        <StyledInfoline
          index={index + 1}
          locale={locale}
          influencer={formattedInfluencers[index]}
          mainUnit={mainUnit}
          screenSize={screenSize}
          delay={(index + 1) * animationDelay}
          clickable={!!onClickInfluencer}
          isTooltipDisabled={isScrolling}
          tooltip={tooltip}
        />
      </div>
    );
  }

  const handleSortChange = useCallback(
    (value) => {
      if (onSort) {
        onSort(value as SortInfluencer);
        onPageReset();
      }
    },
    [onPageReset, onSort]
  );

  useEffect(() => {
    onPageReset();
    setUseDynamicList(false);
  }, [influencers, onPageReset]);

  const label = (type: string, value: SortInfluencer) => {
    const isSelected = sort === value;
    return (
      <Label
        selected={isSelected}
        onClick={() =>
          !!onClickInfluencer && handleSortChange(value as SortInfluencer)
        }
        clickable={!!onSort && !!onClickInfluencer}
      >
        {isSelected && !!onSort && <StyledIcon glyph="arrow-down" />}
        {type}
      </Label>
    );
  };
  const currentLabel =
    sort === Metric.Doc
      ? postsLabel
      : sort === Metric.Reach
      ? estimatedReachLabel
      : engagementLabel;
  return (
    <div className={className} ref={ref}>
      <Header>
        <Accounts largeScreen={screenSize === "large"}>
          <HeaderText clickable={!!onSort && !!onClickInfluencer}>
            {screenSize !== "large" ? (
              <>
                {!!onSort && !!onClickInfluencer ? (
                  <Menu
                    disclosure={{ variant: "tertiary", size: "xs" }}
                    label={tr(locale, "INFLUENCERS::TABLE::COLUMN::RANKING")}
                    values={[
                      { id: Metric.Doc, label: postsLabel },
                      { id: Metric.Reach, label: estimatedReachLabel },
                      { id: Metric.Engagement, label: engagementLabel },
                    ]}
                    selected={sort}
                    onSelect={onSort}
                  />
                ) : (
                  <span>
                    {tr(locale, "PROJECTS_OVERVIEW_SORT_BY")} {currentLabel}
                  </span>
                )}
              </>
            ) : (
              tr(locale, "INFLUENCERS::TABLE::COLUMN::ACCOUNTS")
            )}
          </HeaderText>
        </Accounts>
        {screenSize === "large" && (
          <Labels>
            {label(postsLabel, Metric.Doc)}
            {label(estimatedReachLabel, Metric.Reach)}
            {label(engagementLabel, Metric.Engagement)}
          </Labels>
        )}
      </Header>
      <InfluencersContainer
        height={isFullscreen ? "100%" : `${lineHeight * limit}px`}
      >
        {!useDynamicList ? (
          <>
            {map(
              take(
                formattedInfluencers,
                isFullscreen ? formattedInfluencers.length : page.to
              ),
              (influencer, index) => (
                <div
                  key={index}
                  onClick={
                    onClickInfluencer
                      ? () => onClickInfluencer(influencer)
                      : null
                  }
                >
                  <StyledInfoline
                    index={index + 1}
                    locale={locale}
                    influencer={influencer}
                    mainUnit={mainUnit}
                    screenSize={screenSize}
                    delay={(index + 1) * animationDelay}
                    clickable={!!onClickInfluencer}
                    tooltip={tooltip}
                  />
                </div>
              )
            )}
          </>
        ) : (
          <Spring from={{ top: 0 }} to={{ top: lineHeight * (page.from - 1) }}>
            {(props) => (
              <List
                width={1}
                height={lineHeight * limit}
                rowCount={formattedInfluencers.length}
                rowHeight={lineHeight}
                rowRenderer={rowRenderer}
                containerStyle={{
                  width: "100%",
                  maxWidth: "100%",
                }}
                style={{
                  width: "100%",
                  overflow: "hidden",
                }}
                scrollTop={props.top}
              />
            )}
          </Spring>
        )}
      </InfluencersContainer>
      {showPagination && (
        <div onMouseEnter={() => setUseDynamicList(true)}>
          <StyledPagination
            length={length}
            page={page}
            locale={locale}
            onPageUp={onPageUp}
            onPageDown={onPageDown}
          />
        </div>
      )}
    </div>
  );
};

export default RadarlyWidgetTopAuthors;
